import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB — safe under typical ingress limits

function genUploadId() {
  return 'up_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
}

/**
 * Uploads a file in 5 MB chunks. Bypasses ingress/proxy body-size limits entirely.
 * @param {File} file
 * @param {'content'|'material'} kind  — which storage prefix to use
 * @param {(percent:number)=>void} [onProgress]
 * @returns {Promise<{storage_path:string, filename:string, content_type:string, size:number}>}
 */
export async function chunkedUpload(file, kind = 'content', onProgress) {
  const uploadId = genUploadId();
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(file.size, start + CHUNK_SIZE);
    const slice = file.slice(start, end);
    const fd = new FormData();
    fd.append('chunk', slice, `chunk_${i}`);
    fd.append('upload_id', uploadId);
    fd.append('chunk_index', String(i));
    // Upload with retry (1 retry) for transient errors
    let lastErr = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        await axios.post(`${API}/upload/chunk`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (evt) => {
            if (!onProgress || !evt.total) return;
            const chunkFraction = (evt.loaded / evt.total);
            const overall = ((i + chunkFraction) / totalChunks) * 100;
            onProgress(Math.min(99, Math.round(overall)));
          },
        });
        lastErr = null;
        break;
      } catch (e) {
        lastErr = e;
        if (attempt === 0) await new Promise(r => setTimeout(r, 800));
      }
    }
    if (lastErr) throw lastErr;
  }
  // Finalize — backend reassembles and uploads to storage
  const res = await axios.post(
    `${API}/upload/finalize`,
    {
      upload_id: uploadId,
      total_chunks: totalChunks,
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      kind,
    },
    { withCredentials: true }
  );
  if (onProgress) onProgress(100);
  return res.data;
}
